import React, { useEffect, useState } from "react";
import {
  TextField,
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  Button,
  InputLabel,
} from "@mui/material";
import { useProfile } from "./hooks";
import { z } from "zod";
import Link from "@mui/material/Link";
import { useSelector } from "react-redux";
import { selectUserLoginDetail } from "../../store/auth/authSlice";
import { useTheme, emphasize, styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import HomeIcon from "@mui/icons-material/Home";
import useAuth from "../login/hooks";

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    cursor: "pointer",
    fontWeight: theme.typography.fontWeightRegular,
    "&:hover, &:focus": {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    "&:active": {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
});

const Profile = () => {
  const { detail } = useAuth();
  const { profile: profileData } = useProfile();
  const [isContentAdmin, setIsContentAdmin] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const theme = useTheme();

  useEffect(() => {
    if (detail) {
      const role = detail?.roles?.[0]?.role === "CONTENT_ADMIN" || "TEACHER";
      setIsContentAdmin(role);
    }
  }, [profileData, detail]);

  const toStandardCase = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };
  return (
    <Box
      sx={{
        padding: "0px 20px",
        [theme.breakpoints.down("md")]: {
          padding: "0px",
        },
      }}
    >
      <Breadcrumbs aria-label="breadcrumb">
        <StyledBreadcrumb
          component="a"
          href="/study"
          label="Home"
          icon={<HomeIcon fontSize="small" />}
        />
        <StyledBreadcrumb
          // component="a"
          href="/profile"
          label="Profile"
        />
      </Breadcrumbs>
      <div className="question header-container">
        <div className="header">
          <Typography
            align="left"
            variant="h1"
            color="primary.blue"
            sx={{
              margin: "0",
              fontWeight: "700",
              lineHeight: "1.5",
              fontSize: "2rem",
            }}
          >
            {"Personal Information"}
          </Typography>
        </div>
      </div>
      {isContentAdmin ? (
        <Grid
          sx={styles.gridContainer}
          container
          spacing={2}
          alignItems="center"
        >
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>First Name </InputLabel>
            <TextField
              name="firstName"
              fullWidth
              value={detail?.firstName || ""}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Last Name </InputLabel>
            <TextField
              name="lastName"
              fullWidth
              value={detail?.lastName || ""}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Email </InputLabel>
            <TextField
              name="email"
              fullWidth
              value={detail?.email || ""}
              disabled
              type="email"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Contact </InputLabel>
            <TextField
              name="number"
              fullWidth
              value={detail?.phoneNumber || ""}
              disabled
              type="number"
            />
          </Grid>
        </Grid>
      ) : (
        <Grid
          sx={styles.gridContainer}
          container
          spacing={2}
          alignItems="center"
        >
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>First Name </InputLabel>
            <TextField
              name="firstName"
              fullWidth
              value={profileData?.firstName || ""}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Last Name </InputLabel>
            <TextField
              name="lastName"
              fullWidth
              value={profileData?.lastName || ""}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Email </InputLabel>
            <TextField
              name="firstName"
              fullWidth
              value={profileData?.email || ""}
              disabled
              type="email"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Contact </InputLabel>
            <TextField
              name="firstName"
              fullWidth
              value={profileData?.mobile || ""}
              disabled
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Grade</InputLabel>
            <TextField
              name="grade"
              fullWidth
              value={profileData?.grade || ""}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Section</InputLabel>
            <TextField
              name="section"
              fullWidth
              value={profileData?.section || ""}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Date of Birth</InputLabel>
            <TextField
              name="dob"
              fullWidth
              type="date"
              value={profileData?.dob || ""}
              disabled
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Admission Date</InputLabel>
            <TextField
              name="admissionDate"
              fullWidth
              type="date"
              value={profileData?.admissionDate || ""}
              disabled
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Primary Language</InputLabel>
            <TextField
              name="firstLanguage"
              fullWidth
              value={profileData?.firstLanguage || ""}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>
              Secondary Language
            </InputLabel>
            <TextField
              name="secondLanguage"
              fullWidth
              value={profileData?.secondLanguage || ""}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Gender </InputLabel>
            <TextField
              name="gender"
              fullWidth
              value={
                (profileData?.gender && toStandardCase(profileData.gender)) ||
                ""
              }
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Address </InputLabel>
            <TextField
              name="address"
              fullWidth
              value={profileData?.address || ""}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>
              Learning Generation{" "}
            </InputLabel>
            <TextField
              name="learningGeneration"
              fullWidth
              value={profileData?.learningGeneration || ""}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>Geographical Type</InputLabel>
            <TextField
              name="geographicalType"
              fullWidth
              value={
                (profileData?.geographicalType &&
                  toStandardCase(profileData.geographicalType)) ||
                ""
              }
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>IR Level </InputLabel>
            <TextField
              name="gradeLevel"
              fullWidth
              value={profileData?.gradeLevel || ""}
              disabled
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Profile;

const styles = {
  label: {
    color: "var(--mui-palette-common-black)",
    fontWeight: "bold",
  },
  inputLabel: {
    color: "var(--mui-palette-primary-darkGrey)",
    marginBottom: "5px",
    fontWeight: "600",
    fontSize: "14px",
  },
  gridContainer: {
    mt: 2,
  },
  suggestionBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "10px",
  },
  suggestionItem: {
    padding: "10px",
    border: "1px solid #ccc",
    borderRadius: "2px",
    cursor: "pointer",
    boxShadow: "0 4px 8px var(--mui-palette-secondary-boxShadow1)",
    backgroundColor: "var(--mui-palette-gray-lightGrey300)",
  },
  uploadBtn: {
    width: "30px",
    height: "30px",
    boxShadow: "none",
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    borderRadius: "0",
    minWidth: "30px",
  },
  hiddenInput: {
    display: "none",
  },
  actionBtnItem: {
    marginLeft: "5px",
    marginRight: "5px",
    cursor: "pointer",
    display: "flex",
    gap: "20px",
    alignItems: "center",
  },
  row: {
    padding: "5px 16px",
  },
};
